/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { toET } from '../utils/et';
import { format } from 'date-fns';
import openFile from '../utils/openFile';

const GUIDES = [
  { title: 'Setting Your Availability', desc: 'Weekly pattern + individual day toggles', path: '/api/guides/availability' },
  { title: 'Requesting Time Off', desc: 'Full day or partial day time-off requests', path: '/api/guides/timeoff' },
  { title: 'Completing a Trip', desc: 'Step-by-step flow: en route → pickup → dropoff → complete', path: '/api/guides/trips' },
];

const ADMIN_GUIDES = [
  { title: 'Patient Invoice Flow (Private Pay)', desc: 'How to create, send, and track patient invoices through QuickBooks', path: '/api/admin-guides/patient-invoice' },
];

export default function Training() {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadName, setUploadName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadType, setUploadType] = useState('document'); // document, video_link
  const [videoUrl, setVideoUrl] = useState('');
  const [tab, setTab] = useState('admin'); // admin, guides, documents, videos
  const [deleting, setDeleting] = useState(null);

  const fetchFiles = async () => {
    try {
      const res = await api.get('/api/files/company');
      setFiles(res.data.files || []);
    } catch (err) {}
  };

  useEffect(() => { fetchFiles(); }, []);

  const handleUpload = async () => {
    if (!uploadName.trim()) return;
    setUploading(true);
    try {
      if (uploadType === 'video_link') {
        // Store as a company file with the URL in metadata
        const formData = new FormData();
        // Create a tiny placeholder file for video links
        const blob = new Blob([JSON.stringify({ type: 'video_link', url: videoUrl, title: uploadName })], { type: 'application/json' });
        formData.append('file', blob, `${uploadName}.video.json`);
        formData.append('category', 'company');
        formData.append('doc_type', 'training_video');
        formData.append('notes', videoUrl);
        await api.post('/api/files/upload', formData);
      } else if (uploadFile) {
        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('category', 'company');
        formData.append('doc_type', 'training_document');
        await api.post('/api/files/upload', formData);
      }
      setShowUpload(false);
      setUploadName('');
      setUploadFile(null);
      setVideoUrl('');
      fetchFiles();
    } catch (err) { alert('Upload failed: ' + (err.response?.data?.error || err.message)); }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this file? Drivers will no longer see it.')) return;
    setDeleting(id);
    try {
      await api.delete(`/api/files/${id}`);
      fetchFiles();
    } catch (err) { alert('Delete failed'); }
    setDeleting(null);
  };

  const videoFiles = files.filter(f => f.doc_type === 'training_video');
  const docFiles = files.filter(f => f.doc_type !== 'training_video');

  return (
    <div style={{ padding: 20, maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h2 style={{ color: '#185FA5', margin: 0 }}>Training & Resources</h2>
          <p style={{ color: '#888', fontSize: 13, margin: '4px 0 0' }}>
            Driver guides, training documents, and videos. Drivers see these in their Help tab.
          </p>
        </div>
        <button onClick={() => setShowUpload(true)} style={{
          padding: '8px 16px', background: '#185FA5', color: '#fff', border: 'none',
          borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>+ Add resource</button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {[['admin', 'Admin Guides'], ['guides', 'Driver Guides'], ['documents', `Documents (${docFiles.length})`], ['videos', `Videos (${videoFiles.length})`]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '8px 16px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600,
            background: tab === key ? '#185FA5' : '#f0f0f0', color: tab === key ? '#fff' : '#555', cursor: 'pointer',
          }}>{label}</button>
        ))}
      </div>

      {/* Admin Guides tab */}
      {tab === 'admin' && (
        <div>
          <p style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
            Dispatch-only guides. Drivers cannot see these.
          </p>
          {ADMIN_GUIDES.map((g, i) => (
            <a key={i} href={g.path} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', display: 'block' }}>
              <div style={{
                background: '#fff', border: '1px solid #e9ecef', borderRadius: 10,
                padding: '14px 18px', marginBottom: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#185FA5' }}>{g.title}</div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{g.desc}</div>
                </div>
                <span style={{ fontSize: 12, color: '#185FA5', fontWeight: 500 }}>Preview / Print</span>
              </div>
            </a>
          ))}
        </div>
      )}

      {/* Guides tab */}
      {tab === 'guides' && (
        <div>
          <p style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
            These are the built-in guides shown to drivers. Click to preview or print.
          </p>
          {GUIDES.map((g, i) => (
            <a key={i} href={g.path} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', display: 'block' }}>
              <div style={{
                background: '#fff', border: '1px solid #e9ecef', borderRadius: 10,
                padding: '14px 18px', marginBottom: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#185FA5' }}>{g.title}</div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{g.desc}</div>
                </div>
                <span style={{ fontSize: 12, color: '#185FA5', fontWeight: 500 }}>Preview / Print</span>
              </div>
            </a>
          ))}
        </div>
      )}

      {/* Documents tab */}
      {tab === 'documents' && (
        <div>
          {docFiles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#999', fontSize: 13 }}>
              No training documents uploaded yet. Click "+ Add resource" to upload.
            </div>
          ) : docFiles.map(f => (
            <div key={f.id} style={{
              background: '#fff', border: '1px solid #e9ecef', borderRadius: 10,
              padding: '12px 16px', marginBottom: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{f.original_filename}</div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                  Uploaded {format(toET(f.created_at), 'MMM d, yyyy')}
                  {f.ack_count > 0 && <span style={{ marginLeft: 8, color: '#0F6E56' }}>{f.ack_count} driver(s) viewed</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <a href="#" onClick={(e) => openFile(f.id, e)}
                  style={{ fontSize: 11, padding: '4px 10px', background: '#E6F1FB', color: '#185FA5', border: '1px solid #B5D4F4', borderRadius: 6, textDecoration: 'none' }}>
                  View
                </a>
                <button onClick={() => handleDelete(f.id)} disabled={deleting === f.id}
                  style={{ fontSize: 11, padding: '4px 10px', background: '#FFEBEE', color: '#C62828', border: '1px solid #FFCDD2', borderRadius: 6, cursor: 'pointer' }}>
                  {deleting === f.id ? '...' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Videos tab */}
      {tab === 'videos' && (
        <div>
          {videoFiles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#999', fontSize: 13 }}>
              No training videos added yet. Click "+ Add resource" to add a video link.
            </div>
          ) : videoFiles.map(f => {
            const url = f.notes || '';
            return (
              <div key={f.id} style={{
                background: '#fff', border: '1px solid #e9ecef', borderRadius: 10,
                padding: '12px 16px', marginBottom: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{f.original_filename?.replace('.video.json', '')}</div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                    {url && <a href={url} target="_blank" rel="noreferrer" style={{ color: '#185FA5' }}>{url.substring(0, 50)}</a>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {url && (
                    <a href={url} target="_blank" rel="noreferrer"
                      style={{ fontSize: 11, padding: '4px 10px', background: '#E6F1FB', color: '#185FA5', border: '1px solid #B5D4F4', borderRadius: 6, textDecoration: 'none' }}>
                      Watch
                    </a>
                  )}
                  <button onClick={() => handleDelete(f.id)} disabled={deleting === f.id}
                    style={{ fontSize: 11, padding: '4px 10px', background: '#FFEBEE', color: '#C62828', border: '1px solid #FFCDD2', borderRadius: 6, cursor: 'pointer' }}>
                    {deleting === f.id ? '...' : 'Delete'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload modal */}
      {showUpload && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 420 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>Add training resource</h3>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button onClick={() => setUploadType('document')} style={{
                flex: 1, padding: '8px 12px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                background: uploadType === 'document' ? '#185FA5' : '#f0f0f0', color: uploadType === 'document' ? '#fff' : '#555',
              }}>Document / PDF</button>
              <button onClick={() => setUploadType('video_link')} style={{
                flex: 1, padding: '8px 12px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                background: uploadType === 'video_link' ? '#185FA5' : '#f0f0f0', color: uploadType === 'video_link' ? '#fff' : '#555',
              }}>Video link</button>
            </div>

            {uploadType === 'document' && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>File (PDF, Word, image, etc.)</label>
                <input type="file" onChange={e => { setUploadFile(e.target.files[0]); if (!uploadName && e.target.files[0]) setUploadName(e.target.files[0].name); }} style={{ fontSize: 13 }} />
              </div>
            )}

            {uploadType === 'video_link' && (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Title</label>
                  <input value={uploadName} onChange={e => setUploadName(e.target.value)} placeholder="e.g. How to complete a trip"
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Video URL (YouTube, Loom, Google Drive, etc.)</label>
                  <input value={videoUrl} onChange={e => setVideoUrl(e.target.value)} placeholder="https://..."
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
              </>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => { setShowUpload(false); setUploadFile(null); setUploadName(''); setVideoUrl(''); }}
                style={{ padding: '8px 14px', fontSize: 13, background: '#f0f0f0', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleUpload} disabled={uploading || (!uploadFile && uploadType === 'document') || (!videoUrl && uploadType === 'video_link')}
                style={{ padding: '8px 14px', fontSize: 13, background: '#185FA5', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
